import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {ApiResponseCheckPublicServiceConsentResponse, CreatePublicServiceConsentRequest} from '@di-shared/api/di/__generated';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import {ErrorBoundary} from '@sentry/react';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

export interface PublicServiceConsentsProps {
  vin: string;
}

function PublicServiceConsents(props: PublicServiceConsentsProps) {
  return (
    <ErrorBoundary fallback={<PublicServiceConsentsFallback />}>
      <Suspense fallback={<PublicServiceConsentsLoading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: PublicServiceConsentsProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const {refetchAvailableComponent} = useAvailableComponentKr({vin});
  const puid = useEnvironmentStore((state) => state.puid);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {getFeatureAgreed, getFeatureAgreeDate} = useAvailableComponentKr({vin});

  const {data: publicServiceContentsData} = useSuspenseQuery({
    queryKey: [
      getQueryKey(diApi.publicService.checkPublicServiceConsents),
      puid,
      Object.values(CreatePublicServiceConsentRequest.category),
    ],
    queryFn: () =>
      diApi.publicService.checkPublicServiceConsents({
        puid,
        categoryList: Object.values(CreatePublicServiceConsentRequest.category),
      }),
    select: ({data}: ApiResponseCheckPublicServiceConsentResponse) => {
      return {
        ...data,
        consents: data.consents
          .map((consentItem) => {
            return {...consentItem, consentDate: parseDateWithFormat(consentItem.consentDate)};
          })
          .sort((a, b) => Number(b.status) - Number(a.status)),
      };
    },
  });

  const isFinancialAgree =
    getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT) && getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);
  const financeAgreeDate = parseDateWithFormat(getFeatureAgreeDate(PermissionTypeKr.FINANCIAL_MANAGEMENT));

  const licenseRenewalConsent = getConsentByCategory(CreatePublicServiceConsentRequest.category.LICENSE_RENEWAL);
  const vehicleInspectionConsent = getConsentByCategory(CreatePublicServiceConsentRequest.category.VEHICLE_INSPECTION);
  const highwayUnpaidConsent = getConsentByCategory(CreatePublicServiceConsentRequest.category.HIGHWAY_UNPAID);
  const demeritEducationConsent = getConsentByCategory(CreatePublicServiceConsentRequest.category.DEMERIT_EDUCATION);

  function getConsentByCategory(category: CreatePublicServiceConsentRequest.category) {
    return publicServiceContentsData.consents.find((consentItem) => consentItem.category === category);
  }

  const [withdrawFinanceManagement3rd] = usePendingHandler(
    async () => {
      const isConfirm = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(ROUTE_PATHS.BOTTOM_SHEET_CAPITAL_WITHDRAW.getFullPath());
      if (!isConfirm) {
        return;
      }

      await diApi.financialManagementController.deleteFinancialAgreement({
        requestBody: {puid, vin},
      });
      await refetchAvailableComponent();
      WebviewUtils.showToast(t('T40166'));
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  async function updateFinanceStatus() {
    if (isFinancialAgree) {
      withdrawFinanceManagement3rd();
    } else {
      goToCapitalAgreePage();
    }
  }

  function goToCapitalAgreePage() {
    navigate(ROUTE_PATHS.CAPITAL_TERMS.getFullPath(vin));
  }

  async function openPublicServiceWithdrawBottomSheet(category: CreatePublicServiceConsentRequest.category) {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVCIE_WITHDRAW.getFullPath(), {
      vin,
      category,
    });
    if (result) {
      queryClient.invalidateQueries({
        queryKey: [
          getQueryKey(diApi.publicService.checkPublicServiceConsents),
          puid,
          Object.values(CreatePublicServiceConsentRequest.category),
        ],
      });
    }
  }

  function goToPublicServiceAgreePage(category: CreatePublicServiceConsentRequest.category) {
    // [2025-12-12:cy.seo] FIXME: PPP1 - 이거 이렇게 하면 돌아왔을떄 같은 페이지( PUBLIC_SERVICE_CONSENTS)가 2개씩 쌓여있음. 뒤로가기 하면 안됨
    // 이거부터 새 웹뷰로 열어야함. 그리고 nextUrl 없으면 webview close 하고
    navigate(
      ROUTE_PATHS.PUBLIC_SERVICE_CONSENTS_$CATEGORY.getFullPath({
        vin,
        category,
        nextUrl: ROUTE_PATHS.PUBLIC_SERVICE_CONSENTS.getFullPath(vin),
      })
    );
  }

  function updateConsentStatus(status: boolean, category: CreatePublicServiceConsentRequest.category) {
    if (status) {
      openPublicServiceWithdrawBottomSheet(category);
    } else {
      goToPublicServiceAgreePage(category);
    }
  }

  return (
    <main
      id="content"
      className="content"
    >
      <section className="section setting_agree">
        <h2 className="policy_title">{t('T20352')}</h2>
        <p className="policy_desc">{t('T20356')}</p>
        <ul className="policy_dot_list">
          <li className="policy_dot_item">{t('T20357')}</li>
          <li className="policy_dot_item">{t('T20358')}</li>
          <li className="policy_dot_item">{t('T20359')}</li>
        </ul>
        <ul className="agree_list">
          <PublicServiceConsentItem
            title={t('T40155')}
            status={isFinancialAgree}
            consentDate={financeAgreeDate}
            onClick={() => updateFinanceStatus()}
          />
          {vehicleInspectionConsent && (
            <PublicServiceConsentItem
              title={t('T30528')}
              status={vehicleInspectionConsent.status}
              consentDate={vehicleInspectionConsent.consentDate}
              onClick={() =>
                updateConsentStatus(vehicleInspectionConsent.status, CreatePublicServiceConsentRequest.category.VEHICLE_INSPECTION)
              }
            />
          )}
          {licenseRenewalConsent && (
            <PublicServiceConsentItem
              title={t('T20360')}
              status={licenseRenewalConsent.status}
              consentDate={licenseRenewalConsent.consentDate}
              onClick={() => updateConsentStatus(licenseRenewalConsent.status, CreatePublicServiceConsentRequest.category.LICENSE_RENEWAL)}
            />
          )}
          {demeritEducationConsent && (
            <PublicServiceConsentItem
              title={t('T20361')}
              status={demeritEducationConsent.status}
              consentDate={demeritEducationConsent.consentDate}
              onClick={() =>
                updateConsentStatus(demeritEducationConsent.status, CreatePublicServiceConsentRequest.category.DEMERIT_EDUCATION)
              }
            />
          )}
          {highwayUnpaidConsent && (
            <PublicServiceConsentItem
              title={t('T20362')}
              status={highwayUnpaidConsent.status}
              consentDate={highwayUnpaidConsent.consentDate}
              onClick={() => updateConsentStatus(highwayUnpaidConsent.status, CreatePublicServiceConsentRequest.category.HIGHWAY_UNPAID)}
            />
          )}
        </ul>
      </section>
    </main>
  );
}

function PublicServiceConsentItem({
  title,
  consentDate,
  status,
  onClick,
}: {
  title: string;
  consentDate: Date | null;
  status: boolean;
  onClick(): void;
}) {
  const {t} = useInferableTranslation();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  return (
    <li className="agree_item">
      <div className="toggle_trigger">
        <div className="toggle_text">
          <p className="toggle_title body_large_b">{title}</p>
          <p className="toggle_date body_small_r">
            {t('T20363')} : {consentDate ? DashedDatetimeFormatter.format(consentDate, 'datetime') : '-'}
          </p>
        </div>

        <div className="toggle_box">
          <input
            type="checkbox"
            id={`toggle_${title}`}
            className="input"
            checked={!!status}
            onClick={onClick}
          />
          <label
            htmlFor={`toggle_${title}`}
            className="label"
          >
            {title}
          </label>
        </div>
      </div>
    </li>
  );
}

function PublicServiceConsentsLoading() {
  const {t} = useInferableTranslation();

  return (
    <main
      id="content"
      className="content"
    >
      <section className="section setting_agree">
        <h2 className="policy_title">{t('T20352')}</h2>
        <p className="policy_desc">{t('T20356')}</p>
        <ul className="policy_dot_list">
          <li className="policy_dot_item">{t('T20357')}</li>
          <li className="policy_dot_item">{t('T20358')}</li>
          <li className="policy_dot_item">{t('T20359')}</li>
        </ul>
        <ul
          className="agree_list"
          style={{gap: '14px', display: 'grid'}}
        >
          <li className="agree_item skeleton">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">::</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
              <div className="toggle_box">
                <input
                  type="checkbox"
                  className="input"
                  checked={true}
                />
                <label className="label">::</label>
              </div>
            </div>
          </li>
          <li className="agree_item skeleton">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">::</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
              <div className="toggle_box">
                <input
                  type="checkbox"
                  className="input"
                  checked={true}
                />
                <label className="label">::</label>
              </div>
            </div>
          </li>
          <li className="agree_item skeleton">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">::</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
              <div className="toggle_box">
                <input
                  type="checkbox"
                  className="input"
                  checked={true}
                />
                <label className="label">::</label>
              </div>
            </div>
          </li>
          <li className="agree_item skeleton">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">::</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
              <div className="toggle_box">
                <input
                  type="checkbox"
                  className="input"
                  checked={true}
                />
                <label className="label">::</label>
              </div>
            </div>
          </li>
          <li className="agree_item skeleton">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">::</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
              <div className="toggle_box">
                <input
                  type="checkbox"
                  className="input"
                  checked={true}
                />
                <label className="label">::</label>
              </div>
            </div>
          </li>
        </ul>
      </section>
    </main>
  );
}

function PublicServiceConsentsFallback() {
  const {t} = useInferableTranslation();

  return (
    <main
      id="content"
      className="content"
    >
      <section className="section setting_agree">
        <h2 className="policy_title">{t('T20352')}</h2>
        <p className="policy_desc">{t('T20356')}</p>
        <ul className="policy_dot_list">
          <li className="policy_dot_item">{t('T20357')}</li>
          <li className="policy_dot_item">{t('T20358')}</li>
          <li className="policy_dot_item">{t('T20359')}</li>
        </ul>
        <ul
          className="agree_list"
          style={{gap: '14px', display: 'grid'}}
        >
          <li className="agree_item">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">{t('T40155')}</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
            </div>
          </li>
          <li className="agree_item">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">{t('T30528')}</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
            </div>
          </li>
          <li className="agree_item">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">{t('T20360')}</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
            </div>
          </li>
          <li className="agree_item">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">{t('T20361')}</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
            </div>
          </li>
          <li className="agree_item">
            <div className="toggle_trigger">
              <div className="toggle_text">
                <p className="toggle_title body_large_b">{t('T20362')}</p>
                <p className="toggle_date body_small_r">{t('T20363')} : -</p>
              </div>
            </div>
          </li>
        </ul>
      </section>
    </main>
  );
}

export default PublicServiceConsents;
